import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { routes } from 'constants/routes';
import PersonalProfile from 'components/SignUp/PersonalProfile';
import VerificationCode from 'components/SignUp/VerificationCode';
import { ISignUpProps } from './types';
import { ReactComponent as Logo } from 'assets/RewardsLogo.svg';
import Button from 'components/Buttons/Button';
import {
  useSignUpMutation,
  useVerifyUserMutation,
  useResendCodeMutation,
  useLoginMutation,
} from 'store/apis/signUpApi';
import { useLazyUserQuery } from 'store/apis/userApi';
import { toast } from 'react-hot-toast';
import { useAuth } from 'hooks/useAuth';
import { extractAndStoreReferralCode } from 'utils/common';
import { useMobileKeyboard } from 'hooks/useMobileKeyboard';
import { PENDING_INVITE_TOKEN_KEY, SKIP_ONBOARDING_KEY } from 'containers/InviteHandler';

const SignUp: React.FC<ISignUpProps> = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [verificationType, setVerificationType] = useState<'phone_number' | 'email'>('email');
  const [verificationCode, setVerificationCode] = useState('');
  useMobileKeyboard();

  const [signUp, { isLoading: isSigningUp }] = useSignUpMutation();
  const [verifyUser, { isLoading: isVerifying }] = useVerifyUserMutation();
  const [resendCode] = useResendCodeMutation();
  const [login, { isLoading: isLoggingIn }] = useLoginMutation();
  const [fetchUser, { isLoading: isFetchingUser }] = useLazyUserQuery();

  // Extract invitation parameters from URL
  // URL format: /sign-up?invitationToken=xxx&email=xxx&businessId=xxx
  const invitationToken =
    searchParams.get('invitationToken') || searchParams.get('invitation_token') || null;
  const invitedEmail = searchParams.get('email') || null;
  const invitedBusinessId =
    searchParams.get('businessId') || searchParams.get('business_id') || null;

  // Extract referral code from URL if present
  useEffect(() => {
    extractAndStoreReferralCode();

    // Store invitation details in session storage for post-signup processing
    if (invitationToken) {
      sessionStorage.setItem('invitationToken', invitationToken);
    }
    if (invitedBusinessId) {
      sessionStorage.setItem('invitedBusinessId', invitedBusinessId);
    }
    if (invitedEmail) {
      sessionStorage.setItem('invitedEmail', invitedEmail);
    }
  }, [invitationToken, invitedBusinessId, invitedEmail]);

  // Generate a unique device token for each sign-up attempt
  const getDeviceToken = () => {
    // Always generate a new device token for sign-up
    // The backend doesn't allow reusing the same device token for multiple sign-ups
    const deviceToken = `web-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    return deviceToken;
  };

  const validationSchemaStep1 = Yup.object().shape({
    name: Yup.string()
      .required('Name is required')
      .trim()
      .test('no-empty-spaces', 'Name cannot be only spaces', (value) => {
        return value ? value.trim().length > 0 : false;
      })
      .min(2, 'Name must be at least 2 characters'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    countryCode: Yup.string().required('Country code is required'),
    countryCodeShort: Yup.string(),
    phoneNumber: Yup.string()
      .required('Phone number is required')
      .matches(/^[0-9]+$/, 'Phone number must contain only digits')
      .test('valid-phone-length', function (value) {
        const { countryCodeShort } = this.parent;

        if (!value) return true; // Let required handle empty values

        // Get expected length for the country
        try {
          if (countryCodeShort) {
            const exampleNumber = getExampleNumber(countryCodeShort as any, examples);
            if (exampleNumber) {
              const expectedLength = exampleNumber.nationalNumber.length;

              // Get proper country name from code
              const countryNames: { [key: string]: string } = {
                US: 'United States',
                CA: 'Canada',
                GB: 'United Kingdom',
                AU: 'Australia',
                IN: 'India',
                // Add more as needed
              };
              const displayCountryName = countryNames[countryCodeShort] || countryCodeShort;

              if (value.length !== expectedLength) {
                return this.createError({
                  message: `Please enter a ${expectedLength}-digit ${displayCountryName} phone number`,
                });
              }
              return true;
            }
          }
        } catch (error) {
          // Fallback validation if we can't get example
        }

        // Basic fallback validation (6-15 digits)
        if (value.length < 6) {
          return this.createError({
            message: 'Please enter at least 6 digits',
          });
        }
        if (value.length > 15) {
          return this.createError({
            message: 'Phone number cannot exceed 15 digits',
          });
        }
        return true;
      }),
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 alphanumeric'),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      email: invitedEmail || '', // Pre-fill with invited email if available
      countryCode: '+1',
      countryName: 'United States',
      countryCodeShort: 'US',
      phoneNumber: '',
      password: '',
    },
    enableReinitialize: true, // Allow form to reinitialize when invitedEmail changes
    validationSchema: validationSchemaStep1,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      // Handle form submission for step 1
      handleNext();
    },
  });

  const { values, setFieldValue, errors, touched, handleSubmit } = formik;

  const handleNext = async () => {
    if (currentStep === 1) {
      const errors = await formik.validateForm();
      if (Object.keys(errors).length === 0) {
        try {
          // Get referral code from session storage
          const storedReferralCode = sessionStorage.getItem('referralCode');

          // Prepare signup payload matching the backend structure (multipart/form-data format)
          const signupPayload: any = {
            name: values.name.trim(),
            email: values.email.trim(),
            password: values.password,
            phoneNumber: `${values.countryCode}${values.phoneNumber}`, // Full phone with country code
            phoneCountryExtension: values.countryCode, // e.g., "+1"
            phoneCountryName: values.countryName || 'United States', // Country name
            phoneCountryCode: values.countryCodeShort || 'US', // Two-letter code
            persona: 'consumer',
            state: '',
            stateShortCode: '',
            city: '',
            country: values.countryName || 'United States',
            countryShortCode: values.countryCodeShort || 'US',
            deviceToken: getDeviceToken(), // Generate unique device token for web
          };

          // TODO: Referral code temporarily disabled in signup payload
          // Backend validation blocks signup with invalid codes
          // Will re-enable when backend adds proper support
          // if (storedReferralCode && storedReferralCode.trim()) {
          //   signupPayload.referralCode = storedReferralCode.trim();
          // }

          // Step 1: Call signup API with verifyWith parameter
          const signupResponse = await signUp({
            payload: signupPayload,
            verifyWith: 'email',
          }).unwrap();

          // Step 2: Immediately call login API after successful signup
          const loginResponse = await login({
            email: values.email,
            password: values.password,
          }).unwrap();

          // Step 3: Fetch user details using the UUID from login response
          let fullUserData = loginResponse;
          if (loginResponse.uuid && loginResponse.access) {
            try {
              const userResponse = await fetchUser({
                token: loginResponse.access,
                uuid: loginResponse.uuid,
              }).unwrap();

              // Merge user details with login response
              fullUserData = {
                ...loginResponse,
                ...userResponse,
                name: values.name,
                phoneNumber: `${values.countryCode}${values.phoneNumber}`,
              };
            } catch (userError) {
              // Continue with just login response data
              fullUserData = {
                ...loginResponse,
                name: values.name,
                phoneNumber: `${values.countryCode}${values.phoneNumber}`,
              };
            }

            // Store the access token and user data for authenticated API calls during verification
            localStorage.setItem('signupToken', JSON.stringify(loginResponse.access));
            localStorage.setItem('signupUserData', JSON.stringify(fullUserData));
          }

          // Success! Show verification code sent message and move to verification
          toast.success(
            `Verification code sent to your ${verificationType === 'email' ? 'email' : 'phone number'}`,
          );

          // Move to verification step
          setCurrentStep(2);
        } catch (error: any) {
          // Handle specific error messages from backend
          let errorMessage = 'Sign-up failed. Please try again.';

          if (error?.data) {
            // Check for different error formats
            if (error.data.error) {
              errorMessage = error.data.error;
            } else if (error.data.message) {
              errorMessage = error.data.message;
            } else if (error.data.email) {
              errorMessage = Array.isArray(error.data.email)
                ? error.data.email[0]
                : error.data.email;
            } else if (error.data.phoneNumber) {
              errorMessage = Array.isArray(error.data.phoneNumber)
                ? error.data.phoneNumber[0]
                : error.data.phoneNumber;
            } else if (error.data.password) {
              errorMessage = Array.isArray(error.data.password)
                ? error.data.password[0]
                : error.data.password;
            } else if (typeof error.data === 'string') {
              errorMessage = error.data;
            }
          } else if (error?.message) {
            errorMessage = error.message;
          }

          // Show the actual error message from backend
          toast.error(errorMessage);
        }
      } else {
        formik.setTouched({
          name: true,
          email: true,
          phoneNumber: true,
          password: true,
        });
      }
    }
  };

  const getPostSignupNavigationUrl = () => {
    const pendingInviteToken = sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY);
    const skipOnboarding = sessionStorage.getItem(SKIP_ONBOARDING_KEY);
    if (pendingInviteToken && skipOnboarding) {
      return `/invite/${pendingInviteToken}`;
    }
    return routes.onboardingBusiness;
  };

  const handleVerify = async (code: string) => {
    try {
      // Prepare the full phone number with country code
      const fullPhoneNumber = `${values.countryCode}${values.phoneNumber}`;

      // Prepare verification payload with correct field names
      const verificationPayload = {
        otp_code: code,
        sendTo: verificationType,
        email: values.email,
        phoneNumber: fullPhoneNumber,
        giftInviteToken: invitationToken, // Pass invitation token if user came from invite email
      };

      // Step 1: Call verification API
      const verifyResponse = await verifyUser(verificationPayload).unwrap();

      // Get the stored token from signup/login
      const storedToken = localStorage.getItem('signupToken');

      if (storedToken && verifyResponse.uuid) {
        // Step 2: Fetch fresh user details after verification
        try {
          const userResponse = await fetchUser({
            token: JSON.parse(storedToken),
            uuid: verifyResponse.uuid,
          }).unwrap();

          // userResponse is an array, get the first element
          const userData = Array.isArray(userResponse) ? userResponse[0] : userResponse;

          // Merge verification response with user details
          const authResponse = {
            data: {
              access: JSON.parse(storedToken),
              refresh: verifyResponse.refresh || '',
              email: (userData as any)?.email || values.email,
              name: values.name,
              phoneNumber: fullPhoneNumber,
              uuid: verifyResponse.uuid,
              business: (userData as any)?.business,
              profile: (userData as any)?.profile,
              isAdmin: (userData as any)?.isAdmin,
              isBlocked: (userData as any)?.isBlocked,
              isRestricted: (userData as any)?.isRestricted,
              lastOtpDetails: verifyResponse.lastOtpDetails,
              deviceOs: (userData as any)?.deviceOs,
              deviceRegId: (userData as any)?.deviceRegId,
              ...(userData as any),
              ...verifyResponse,
            },
          };

          // Use auth.login to properly set user in context and navigate
          await auth?.login(authResponse, getPostSignupNavigationUrl());

          // Remove the temporary signup data
          localStorage.removeItem('signupToken');
          localStorage.removeItem('signupUserData');
        } catch (userError) {
          // Continue with verification response only
          const authResponse = {
            data: {
              access: JSON.parse(storedToken),
              refresh: verifyResponse.refresh || '',
              email: values.email,
              name: values.name,
              phoneNumber: fullPhoneNumber,
              uuid: verifyResponse.uuid,
              ...verifyResponse,
            },
          };

          await auth?.login(authResponse, getPostSignupNavigationUrl());
          localStorage.removeItem('signupToken');
          localStorage.removeItem('signupUserData');
        }
      } else {
        // Fallback: Call login and fetch user if token is not found
        const loginResponse = await login({
          email: values.email,
          password: values.password,
        }).unwrap();

        // Fetch user details if we have uuid
        let userData = loginResponse;
        if (loginResponse.uuid && loginResponse.access) {
          try {
            const userResponse = await fetchUser({
              token: loginResponse.access,
              uuid: loginResponse.uuid,
            }).unwrap();

            userData = {
              ...loginResponse,
              ...userResponse,
              name: values.name,
              phoneNumber: fullPhoneNumber,
            };
          } catch (userError) {
            // Failed to fetch user details in fallback
          }
        }

        const authResponse = {
          data: {
            access: userData.access,
            refresh: userData.refresh,
            email: userData.email || values.email,
            name: values.name,
            phoneNumber: userData.phoneNumber || fullPhoneNumber,
            uuid: userData.uuid,
            business: userData.business,
            profile: userData.profile,
            isAdmin: userData.isAdmin,
            isBlocked: userData.isBlocked,
            isRestricted: userData.isRestricted,
            lastOtpDetails: userData.lastOtpDetails,
            deviceOs: userData.deviceOs,
            deviceRegId: userData.deviceRegId,
            ...userData,
          },
        };

        await auth?.login(authResponse, getPostSignupNavigationUrl());
      }

      // Success! Show success message
      toast.success('Account verified successfully!');
    } catch (error: any) {
      // Handle specific error messages from backend
      let errorMessage = 'Verification failed. Please check your code and try again.';

      if (error?.status === 500) {
        errorMessage = 'Server error occurred. Please try again or contact support.';
      } else if (error?.data) {
        if (error.data.error) {
          errorMessage = error.data.error;
        } else if (error.data.message) {
          errorMessage = error.data.message;
        } else if (error.data.detail) {
          errorMessage = error.data.detail;
        } else if (error.data.otp_code) {
          errorMessage = Array.isArray(error.data.otp_code)
            ? error.data.otp_code[0]
            : error.data.otp_code;
        } else if (typeof error.data === 'string') {
          errorMessage = error.data;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    }
  };

  const handleGoBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else {
      navigate(routes.login);
    }
  };

  const handleCancel = () => {
    navigate(routes.login);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalProfile
            values={values}
            setFieldValue={setFieldValue}
            errors={errors}
            touched={touched}
          />
        );
      case 2:
        return (
          <VerificationCode
            phoneNumber={`${values.countryCode}(${values.phoneNumber.slice(0, 3)})-${values.phoneNumber.slice(3)}`}
            email={values.email}
            onVerify={handleVerify}
            isVerifying={isVerifying}
            verificationType={verificationType}
            onCodeChange={setVerificationCode}
            onSwitchVerificationType={async () => {
              // Toggle between phone_number and email
              const newVerificationType =
                verificationType === 'phone_number' ? 'email' : 'phone_number';

              setVerificationType(newVerificationType);

              // Automatically send OTP to the new verification method
              try {
                const fullPhoneNumber = `${values.countryCode}${values.phoneNumber}`;

                const resendPayload = {
                  sendTo: newVerificationType,
                  email: values.email,
                  phoneNumber: fullPhoneNumber,
                  persona: 'consumer',
                };

                await resendCode(resendPayload).unwrap();

                toast.success(
                  newVerificationType === 'phone_number'
                    ? 'Verification code sent to your phone!'
                    : 'Verification code sent to your email!',
                );
              } catch (error: any) {
                toast.error('Failed to send verification code. Please try again.');
              }
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col overflow-x-hidden mobile-viewport">
      {/* Mobile Header with Back Button - Fixed at Top */}
      <div className="md:hidden mobile-header-fixed px-4 flex items-center gap-3 flex-shrink-0 w-full py-4">
        <button
          onClick={handleCancel}
          className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Go back"
        >
          <svg
            className="w-6 h-6 text-gray-900"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <h1 className="font-semibold text-gray-900 text-lg">Your user profile</h1>
      </div>

      {/* Desktop Logo */}
      <div className="hidden md:block px-8 py-4 border-b border-gray-200">
        <Logo width={140} height={40} />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-x-hidden">
        <div className="w-full mx-auto flex flex-col flex-1 md:flex-none overflow-x-hidden">
          {/* Content Card */}
          <div className="bg-white flex flex-col flex-1 md:flex-none w-full overflow-x-hidden mobile-content-with-fixed-header">
            {/* Step Content */}
            <div className="flex-1 overflow-x-hidden px-4 md:px-8 w-full py-4 md:py-6 pb-24 md:pb-6">
              <div style={{ maxWidth: '1320px', margin: '0 auto' }}>
                {/* Desktop Header - Full width background with aligned text */}
                <div className="hidden md:block mb-6 mt-12 -mx-4 md:-mx-8">
                  <div className="rounded-xl bg-lightPurple py-4 px-4 md:px-8">
                    <div style={{ maxWidth: '1320px', margin: '0 auto' }}>
                      <h1 className="text-lg font-semibold text-secondary leading-none font-poppins">
                        User Onboarding
                      </h1>
                    </div>
                  </div>
                </div>

                {renderStepContent()}

                {/* Desktop Navigation Buttons - Properly aligned with form content */}
                <div className="hidden md:flex justify-between items-start mt-6 w-full">
                  {/* Left side - Go back and Cancel buttons aligned below Terms & Privacy text */}
                  <div className="flex gap-4 items-center">
                    <Button
                      type="button"
                      label="Go back"
                      onClick={handleGoBack}
                      variant="secondary"
                      customClass="!mb-0 !me-0 w-[120px]"
                    />
                  </div>

                  {/* Right side - Submit/Verify button aligned with Password field */}
                  <div className="flex justify-end">
                    {currentStep === 1 ? (
                      <Button
                        type="button"
                        label={
                          isSigningUp || isLoggingIn || isFetchingUser ? 'Signing up...' : 'Submit'
                        }
                        onClick={() => handleSubmit()}
                        disabled={isSigningUp || isLoggingIn || isFetchingUser}
                        loading={isSigningUp || isLoggingIn || isFetchingUser}
                        variant="primary"
                        customClass="!mb-0 !me-0 w-[140px]"
                      />
                    ) : (
                      <Button
                        type="button"
                        label={
                          isVerifying || isLoggingIn || isFetchingUser ? 'Verifying...' : 'Verify'
                        }
                        onClick={() => {
                          if (verificationCode.length === 4) {
                            handleVerify(verificationCode);
                          } else {
                            toast.error('Please enter the complete 4-digit verification code');
                          }
                        }}
                        disabled={
                          isVerifying ||
                          isLoggingIn ||
                          isFetchingUser ||
                          verificationCode.length !== 4
                        }
                        loading={isVerifying || isLoggingIn || isFetchingUser}
                        variant="primary"
                        customClass="!mb-0 !me-0 w-[140px]"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Navigation Buttons - Fixed at Bottom */}
            <div className="md:hidden mobile-nav-buttons flex bg-white w-full gap-3 p-4">
              <Button
                type="button"
                label="Cancel"
                onClick={handleCancel}
                variant="secondary"
                customClass="!mb-0 !me-0 flex-1 min-h-[48px]"
              />
              {currentStep === 1 ? (
                <Button
                  type="button"
                  label={isSigningUp || isLoggingIn || isFetchingUser ? 'Signing up...' : 'Submit'}
                  onClick={() => handleSubmit()}
                  disabled={isSigningUp || isLoggingIn || isFetchingUser}
                  loading={isSigningUp || isLoggingIn || isFetchingUser}
                  variant="primary"
                  customClass="!mb-0 !me-0 flex-1 min-h-[48px]"
                />
              ) : (
                <Button
                  type="button"
                  label={isVerifying || isLoggingIn || isFetchingUser ? 'Verifying...' : 'Verify'}
                  onClick={() => {
                    if (verificationCode.length === 4) {
                      handleVerify(verificationCode);
                    } else {
                      toast.error('Please enter the complete 4-digit verification code');
                    }
                  }}
                  disabled={
                    isVerifying || isLoggingIn || isFetchingUser || verificationCode.length !== 4
                  }
                  loading={isVerifying || isLoggingIn || isFetchingUser}
                  variant="primary"
                  customClass="!mb-0 !me-0 flex-1 min-h-[48px]"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
